import { useEffect, useState } from "react";
import { IndianRupee, ShoppingCart, Users, Package, Clock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { fmtINR, fmtDateTime } from "../../lib/api";
import { StatCard, PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows } from "../../components/Shared";

export default function StoreDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/store-admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <LoadingRows rows={5} />;

  return (
    <div data-testid="store-dashboard">
      <PageHeader title={s.store.name} subtitle="Your store at a glance." />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <StatCard icon={IndianRupee} label="Revenue" value={fmtINR(s.revenue)} testid="stat-revenue" />
        <StatCard icon={ShoppingCart} label="Orders" value={s.orders} testid="stat-orders" delay={0.05} />
        <StatCard icon={Users} label="Customers" value={s.customers} testid="stat-customers" delay={0.1} />
        <StatCard icon={Package} label="Products" value={s.products} testid="stat-products" delay={0.15} />
        <StatCard icon={Clock} label="Pending Orders" value={s.pending_orders} testid="stat-pending" delay={0.2} />

        <div data-testid="store-chart" className="md:col-span-3 lg:col-span-3 bg-card border border-border rounded-md p-6">
          <span className="overline-label">Sales — last 14 days</span>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={s.series} margin={{ left: 0, right: 8, top: 4 }}>
                <defs>
                  <linearGradient id="store-rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9A96E" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C9A96E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, name) => [name === "revenue" ? fmtINR(v) : v, name === "revenue" ? "Revenue" : "Orders"]} />
                <Area type="monotone" dataKey="revenue" stroke="#C9A96E" strokeWidth={2.5} fill="url(#store-rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="top-products" className="md:col-span-3 lg:col-span-2 bg-card border border-border rounded-md p-6">
          <span className="overline-label">Best sellers</span>
          <div className="mt-4 space-y-4">
            {s.top_products.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No sales yet</p>}
            {s.top_products.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <span className="font-display font-extrabold text-muted-foreground w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.qty} sold</p>
                </div>
                <span className="text-sm font-bold">{fmtINR(p.revenue)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-3 lg:col-span-5">
          <span className="overline-label block mb-4">Recent orders</span>
          {s.recent_orders.length === 0 ? (
            <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">No orders yet.</div>
          ) : (
            <TableShell testid="store-recent-orders" head={["Order", "Customer", "Items", "Total", "Payment", "Status", "Placed"]}>
              {s.recent_orders.map((o) => (
                <TR key={o.id} testid={`store-order-${o.id}`}>
                  <TD className="font-mono text-xs font-bold">{o.order_number}</TD>
                  <TD className="text-sm">{o.customer?.name}</TD>
                  <TD className="text-sm">{o.items?.length}</TD>
                  <TD className="font-bold">{fmtINR(o.total)}</TD>
                  <TD><StatusBadge status={o.payment_status} /></TD>
                  <TD><StatusBadge status={o.status} /></TD>
                  <TD className="text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      </div>
    </div>
  );
}
